import React, { useState } from 'react';
import {
    IonContent, IonSpinner, IonText,
    IonCard, IonCardContent, IonIcon, IonButton,
} from '@ionic/react';
import { useIonViewWillEnter } from '@ionic/react';
import {
    peopleOutline, chatbubbleOutline, ribbonOutline, lockClosedOutline,
    mailOutline, imageOutline, documentTextOutline, phonePortraitOutline,
    addCircleOutline,
} from 'ionicons/icons';
import PageLayout from '@/components/navigation/PageLayout';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { apiService } from '@/services/ApiService';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import permissionService from '@/services/PermissionService';
import { OnlineUserResponse, LicenseInfoResponse, EnterprisePreferencesResponse, LoginImageResponse, OfflineAppSettingsResponse } from '@/types/response/enterpriseResponses';
import SignedInUsersModal from '@/components/admin/enterprise-management/SignedInUsersModal';
import SendMessageModal from '@/components/admin/enterprise-management/SendMessageModal';
import EnterpriseImageModal from '@/components/admin/enterprise-management/EnterpriseImageModal';
import AddModuleModal from '@/components/admin/enterprise-management/AddModuleModal';
import SettingsModal from '@/components/common/SettingsModal';
import * as ema from '@/services/util/adapters/enterpriseManagementAdapters';

const EnterpriseManagement: React.FC = () => {
    usePageTitle('Enterprise Management');
    const [isLoading, setIsLoading] = useState(false);
    const [onlineUsers, setOnlineUsers] = useState<OnlineUserResponse[]>([]);
    const [licenseInfo, setLicenseInfo] = useState<LicenseInfoResponse | null>(null);
    const [preferences, setPreferences] = useState<EnterprisePreferencesResponse | null>(null);
    const [loginImages, setLoginImages] = useState<LoginImageResponse[]>([]);
    const [offlineSettings, setOfflineSettings] = useState<OfflineAppSettingsResponse | null>(null);
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
    const [toast, setToast] = useState<{ message: string; variant: ToasterVariant } | null>(null);

    // Modals
    const [showSignedIn, setShowSignedIn] = useState(false);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [showSendMessage, setShowSendMessage] = useState(false);
    const [showLicense, setShowLicense] = useState(false);
    const [showPasswordSignIn, setShowPasswordSignIn] = useState(false);
    const [showEmailSettings, setShowEmailSettings] = useState(false);
    const [showImage, setShowImage] = useState(false);
    const [showSignInMessage, setShowSignInMessage] = useState(false);
    const [showOfflineSettings, setShowOfflineSettings] = useState(false);
    const [showAddModule, setShowAddModule] = useState(false);

    const isSuperAdmin = permissionService.isSuperAdmin();
    const canEditAttachments = permissionService.hasRole('EditAttachments');

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [license, prefs, images, offline] = await Promise.all([
                apiService.getLicenseInfo(),
                apiService.getEnterprisePreferences(),
                apiService.getLoginImages(),
                apiService.getOfflineAppSettings(),
            ]);
            setLicenseInfo(license);
            setPreferences(prefs);
            setLoginImages(images);
            setOfflineSettings(offline);
        } catch {
            setToast({ message: 'Failed to load enterprise data.', variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const loadOnlineUsers = async () => {
        setIsLoadingUsers(true);
        try {
            const users = await apiService.getOnlineUsers();
            setOnlineUsers(users);
        } catch {
            setToast({ message: 'Failed to load signed-in users.', variant: 'error' });
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const openSignedInUsers = () => {
        loadOnlineUsers();
        setShowSignedIn(true);
    };

    useIonViewWillEnter(() => {
        loadData();
    });

    interface TaskCard {
        id: string;
        icon: string;
        title: string;
        description: string;
        show: boolean;
        onClick: () => void;
    }

    const tasks: TaskCard[] = [
        {
            id: 'signed-in-users',
            icon: peopleOutline,
            title: 'Signed In Users',
            description: 'View users currently signed in to SeeSOR',
            show: true,
            onClick: openSignedInUsers,
        },
        {
            id: 'send-message',
            icon: chatbubbleOutline,
            title: 'Send Message',
            description: 'Send a message to selected signed-in users',
            show: true,
            onClick: () => setShowSendMessage(true),
        },
        {
            id: 'license-info',
            icon: ribbonOutline,
            title: 'License Information',
            description: 'View and manage license limits and expiration',
            show: true,
            onClick: () => setShowLicense(true),
        },
        {
            id: 'password-signin',
            icon: lockClosedOutline,
            title: 'Password & Sign In Parameters',
            description: 'Configure password policies and idle timeout settings',
            show: true,
            onClick: () => setShowPasswordSignIn(true),
        },
        {
            id: 'email-settings',
            icon: mailOutline,
            title: 'Email Settings',
            description: 'Configure SMTP or SeeSOR-managed email',
            show: true,
            onClick: () => setShowEmailSettings(true),
        },
        {
            id: 'enterprise-image',
            icon: imageOutline,
            title: 'Enterprise Image',
            description: 'Upload and select the login page image',
            show: true,
            onClick: () => setShowImage(true),
        },
        {
            id: 'signin-message',
            icon: documentTextOutline,
            title: 'Sign In Page Message',
            description: 'Edit the message shown below the sign-in form',
            show: true,
            onClick: () => setShowSignInMessage(true),
        },
        {
            id: 'offline-app-settings',
            icon: phonePortraitOutline,
            title: 'Offline Inspection App Settings',
            description: 'Toggle offline app download availability',
            show: true,
            onClick: () => setShowOfflineSettings(true),
        },
        {
            id: 'add-module',
            icon: addCircleOutline,
            title: 'Add Module',
            description: 'Create a new SeeSOR contract module',
            show: isSuperAdmin,
            onClick: () => setShowAddModule(true),
        },
    ];

    const visibleTasks = tasks.filter(t => t.show);

    return (
        <PageLayout>
            <IonContent>
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                        <IonSpinner />
                    </div>
                ) : (
                    <div style={{ padding: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
                            {visibleTasks.map(task => (
                                <IonCard key={task.id} button onClick={task.onClick}
                                    style={{ margin: 0, cursor: 'pointer', borderRadius: '10px' }}>
                                    <IonCardContent style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1rem' }}>
                                        <IonIcon icon={task.icon} style={{ fontSize: '1.8rem', color: 'var(--ion-color-primary)', flexShrink: 0, marginTop: '0.1rem' }} />
                                        <div>
                                            <div style={{ fontWeight: '600', fontSize: '0.95rem', marginBottom: '0.25rem' }}>{task.title}</div>
                                            <div style={{ fontSize: '0.8rem', color: 'var(--ion-color-medium)' }}>{task.description}</div>
                                        </div>
                                    </IonCardContent>
                                </IonCard>
                            ))}
                        </div>

                        {selectedUserIds.length > 0 && (
                            <div style={{ marginTop: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: 'var(--ion-color-primary-tint)', borderRadius: '8px' }}>
                                <IonText style={{ flex: 1, fontSize: '0.9rem' }}>
                                    {selectedUserIds.length} user{selectedUserIds.length > 1 ? 's' : ''} selected for messaging
                                </IonText>
                                <IonButton size="small" fill="outline" color="primary" onClick={() => setShowSendMessage(true)}>
                                    Send Message
                                </IonButton>
                                <IonButton size="small" fill="clear" color="medium" onClick={() => setSelectedUserIds([])}>
                                    Clear
                                </IonButton>
                            </div>
                        )}
                    </div>
                )}

                {/* Modals */}
                <SignedInUsersModal
                    isOpen={showSignedIn}
                    onDidDismiss={() => setShowSignedIn(false)}
                    users={onlineUsers}
                    isLoading={isLoadingUsers}
                    selectedIds={selectedUserIds}
                    onSelectionChange={ids => { setSelectedUserIds(ids); setShowSignedIn(false); }}
                />

                <SendMessageModal
                    isOpen={showSendMessage}
                    onDidDismiss={() => setShowSendMessage(false)}
                    onlineUsers={onlineUsers}
                    selectedUserIds={selectedUserIds}
                />

                <SettingsModal<ema.LicenseInfoForm>
                    isOpen={showLicense}
                    onDidDismiss={() => setShowLicense(false)}
                    title="License Information" fields={ema.licenseInfoFields} emptyData={ema.licenseInfoEmptyForm}
                    initialData={ema.licenseInfoInitialData(licenseInfo)} onSave={ema.saveLicenseInfo}
                    canEdit={isSuperAdmin} width="480px" height="380px"
                    onSaved={loadData}
                />

                <SettingsModal<ema.PasswordSignInForm>
                    isOpen={showPasswordSignIn}
                    onDidDismiss={() => setShowPasswordSignIn(false)}
                    title="User Password & Sign In Parameters" fields={ema.passwordSignInFields} emptyData={ema.passwordSignInEmptyForm}
                    initialData={ema.passwordSignInInitialData(preferences)} onSave={ema.savePasswordSignIn}
                    width="520px" height="70vh"
                    onSaved={loadData}
                />

                <SettingsModal<ema.EmailSettingsForm>
                    isOpen={showEmailSettings}
                    onDidDismiss={() => setShowEmailSettings(false)}
                    title="Email Settings" fields={ema.emailSettingsFields} emptyData={ema.emailSettingsEmptyForm}
                    initialData={ema.emailSettingsInitialData(preferences)} onSave={ema.saveEmailSettings}
                    width="500px" height="480px"
                    onSaved={loadData}
                />

                <EnterpriseImageModal
                    isOpen={showImage}
                    onDidDismiss={() => setShowImage(false)}
                    images={loginImages}
                    canEditAttachments={canEditAttachments}
                    onSaved={loadData}
                />

                <SettingsModal<ema.SignInMessageForm>
                    isOpen={showSignInMessage}
                    onDidDismiss={() => setShowSignInMessage(false)}
                    title="Sign In Page Message" fields={ema.signInMessageFields} emptyData={ema.signInMessageEmptyForm}
                    initialData={ema.signInMessageInitialData(preferences)} onSave={ema.saveSignInMessage}
                    width="500px" height="320px"
                    onSaved={loadData}
                />

                <SettingsModal<ema.OfflineAppSettingsForm>
                    isOpen={showOfflineSettings}
                    onDidDismiss={() => setShowOfflineSettings(false)}
                    title="Offline Inspection App Settings" fields={ema.offlineAppSettingsFields(isSuperAdmin)} emptyData={ema.offlineAppSettingsEmptyForm}
                    initialData={ema.offlineAppSettingsInitialData(offlineSettings)} onSave={ema.saveOfflineAppSettings}
                    width="460px" height="300px"
                    onSaved={loadData}
                />

                <AddModuleModal
                    isOpen={showAddModule}
                    onDidDismiss={() => setShowAddModule(false)}
                    onSaved={loadData}
                />

                <Toaster isOpen={!!toast} message={toast?.message} variant={toast?.variant} onDidDismiss={() => setToast(null)} />
            </IonContent>
        </PageLayout>
    );
};

export default EnterpriseManagement;
